import { Insight } from '../../../components/office';
import { useFollowedSite } from '../../../contexts/SiteContext';
import { Notice } from '@verlo/shared/components/ui/notice';
import { BRAND } from '@verlo/shared/lib/brand';
import { useEffect, useMemo, useState } from 'react';
import {
  Receipt, ChevronLeft, ChevronRight, Save, Sparkles, ArrowUpRight, Printer,
  TrendingUp, TrendingDown, Loader2, RotateCcw, Lock, 
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { reportsIntelApi } from '@verlo/shared/services/api';
import BenchmarkPicker from '../../../components/reports/BenchmarkPicker';
import { Input } from '@verlo/shared/components/ui/input';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMoney = (v) => v == null ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const fmtMoneySigned = (v) => v == null ? '—' : `${v >= 0 ? '+' : '−'}£${Math.abs(Math.round(v)).toLocaleString('en-GB')}`;
const fmtPct = (v) => v == null ? '—' : `${v.toFixed(1)}%`;
const fmtPctSigned = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}pp`;

const stepMonth = (year, month, delta) => {
  let m = month + delta;
  let y = year;
  while (m < 1)  { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return [y, m];
};

const PnlPage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [siteId, setSiteId] = useFollowedSite('all');
  const [sites, setSites] = useState([]);
  const [data, setData] = useState(null);
  const [waterfall, setWaterfall] = useState(null);
  const [summary, setSummary] = useState(null);
  const [budgetVariance, setBudgetVariance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // { line_key: { value?, kind?, note? } }
  const [saving, setSaving] = useState(false);

  // ---------- Load sites once ----------
  useEffect(() => {
    (async () => {
      try {
        const s = await reportsIntelApi.listSites();
        setSites(s.data.sites || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // ---------- Load lines + summary + waterfall ----------
  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month, ...(siteId !== 'all' ? { site_id: siteId } : {}) };
      const [linesRes, summaryRes] = await Promise.all([
        reportsIntelApi.pnlLines(params),
        reportsIntelApi.pnlSummary(params),
      ]);
      setData(linesRes.data);
      setSummary(summaryRes.data);
      // Waterfall = from previous month → current
      const [py, pm] = stepMonth(year, month, -1);
      const wf = await reportsIntelApi.pnlWaterfall({
        ...(siteId !== 'all' ? { site_id: siteId } : {}),
        from_year: py, from_month: pm, to_year: year, to_month: month,
      });
      setWaterfall(wf.data);
      // Budget variance (Wave 2 → integrated into the P&L workbench).
      // Only meaningful for a single site; skip for "All sites".
      if (siteId !== 'all') {
        try {
          const bv = await reportsIntelApi.getBudgetVariance(siteId, year, month);
          setBudgetVariance(bv.data);
        } catch { setBudgetVariance(null); }
      } else {
        setBudgetVariance(null);
      }
      setEdits({});
    } catch (e) {
      toast.error('Could not load P&L');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, siteId]);

  // ---------- Compute live totals with un-saved edits applied ----------
  const liveLines = useMemo(() => {
    if (!data) return [];
    return data.lines.map((l) => {
      const e = edits[l.key];
      if (!e) return l;
      const value = e.value !== undefined ? e.value : (l.manual_override ? l.value : l.auto_value);
      return {
        ...l,
        value: value === null || value === '' ? 0 : Number(value),
        kind: e.kind || l.kind,
        note: e.note !== undefined ? e.note : l.note,
        manual_override: e.value !== undefined || l.manual_override,
        _dirty: true,
      };
    });
  }, [data, edits]);

  // A month under way is compared with the same days of the month before it.
  const vs = summary?.previous?.to_date ? 'same days last month' : 'last month';
  const liveTotals = useMemo(() => {
    if (!liveLines.length) return data?.totals;
    let revenue = 0, cogs = 0, opex = 0, below = 0, fixed = 0, variable = 0;
    for (const l of liveLines) {
      if (l.category === 'revenue') revenue += l.value;
      else if (l.category === 'cogs') cogs += l.value;
      else if (l.category === 'opex') opex += l.value;
      else if (l.category === 'below') below += l.value;
      if (l.category !== 'revenue') {
        if (l.kind === 'fixed') fixed += l.value;
        else variable += l.value;
      }
    }
    const gross_profit = revenue - cogs;
    const ebitda = gross_profit - opex;
    const net_profit = ebitda - below;
    return {
      revenue, cogs, opex, below,
      gross_profit, ebitda, net_profit,
      gp_margin: revenue > 0 ? (gross_profit / revenue) * 100 : 0,
      ebitda_margin: revenue > 0 ? (ebitda / revenue) * 100 : 0,
      net_margin: revenue > 0 ? (net_profit / revenue) * 100 : 0,
      fixed_costs: fixed, variable_costs: variable,
    };
  }, [liveLines, data]);

  const dirty = Object.keys(edits).length > 0;

  // Cost coverage warning — show banner when menu items have no cost set, or imported
  // lines have no dish to take a cost from.
  // Note: if a missing-cost item happens to have £0 revenue, cost_coverage_pct will
  // read 100% and we hide the banner. That's intentional — silent items don't bias
  // the GP number, so we don't need to nag the operator about them.
  const costCoverage = data?.data_quality?.cost_coverage_pct;
  const missingCount = data?.data_quality?.menu_items_missing_cost || 0;
  const unmatchedCount = data?.data_quality?.lines_unmatched || 0;
  const showCostWarning = (missingCount > 0 || unmatchedCount > 0) && costCoverage !== undefined && costCoverage < 100;
  const costWarningTitle = [
    missingCount > 0 && `${missingCount} menu item${missingCount === 1 ? ' is' : 's are'} missing a cost`,
    unmatchedCount > 0 && `${unmatchedCount} imported line${unmatchedCount === 1 ? ' has' : 's have'} no matching dish`,
  ].filter(Boolean).join(', and ');

  // ---------- Group lines for rendering ----------
  const groups = useMemo(() => {
    const out = {};
    for (const l of liveLines) {
      if (!out[l.group]) out[l.group] = [];
      out[l.group].push(l);
    }
    return out;
  }, [liveLines]);

  // ---------- Save ----------
  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const lines = Object.entries(edits).map(([line_key, e]) => ({
        line_key,
        value: e.value !== undefined ? (e.value === '' ? null : Number(e.value)) : undefined,
        kind: e.kind,
        note: e.note,
      }));
      await reportsIntelApi.pnlSaveLines({
        site_id: siteId === 'all' ? null : siteId,
        year, month, lines,
      });
      toast.success(`Saved ${lines.length} line${lines.length === 1 ? '' : 's'}`, { id: 'pnl-save' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };


  // ---------- Helpers for edit interactions ----------
  const editLine = (key, patch) => setEdits((e) => ({ ...e, [key]: { ...(e[key] || {}), ...patch } }));
  const resetLine = (key) => setEdits((e) => {
    const { [key]: _, ...rest } = e;
    return rest;
  });

  return (
    <ReportsLayout>
      <div data-testid="pnl-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              Revenue, COGS, labour and wastage are auto-pulled from Verlo. Plug in rent, marketing, insurance, and watch EBITDA recalculate live.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SitePicker sites={sites} value={siteId} onChange={setSiteId} allowAll triggerClassName="h-9 w-[180px]" testId="pnl-site-select" />
            <div className="flex items-center gap-1 bg-white border border-hairline rounded-md">
              <button onClick={() => { const [y, m] = stepMonth(year, month, -1); setYear(y); setMonth(m); }} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid="pnl-prev-month">
                <ChevronLeft size={14} />
              </button>
              <span className="px-3 text-[13px] font-semibold text-ink font-mono-display select-none" data-testid="pnl-period-label">
                {MONTH_LABELS[month - 1]} {year}
              </span>
              <button onClick={() => { const [y, m] = stepMonth(year, month, 1); setYear(y); setMonth(m); }} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid="pnl-next-month">
                <ChevronRight size={14} />
              </button>
            </div>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => window.print()} data-testid="pnl-print">
              <Printer size={13} /> Print
            </Button>
            {dirty && (
              <>
                <Button variant="outline" size="sm" className="h-9" onClick={() => setEdits({})} data-testid="pnl-discard">
                  Discard
                </Button>
                <Button className="h-9 bg-verlo hover:bg-verlo-hover gap-1.5" onClick={onSave} disabled={saving} data-testid="pnl-save">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Save {Object.keys(edits).length} change{Object.keys(edits).length === 1 ? '' : 's'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Cost coverage warning */}
        {showCostWarning && (
          <Notice tone="warn" word="Costs" title={costWarningTitle} className="mb-4" data-testid="pnl-cost-warning">
            Their cost isn't counted, so COGS reads low and gross profit high. Cost coverage: <span className="font-mono-display font-semibold">{costCoverage.toFixed(1)}%</span> of revenue.
            {missingCount > 0 && <> Set per-item costs in <a href="/back-office/menu" className="underline">Menu management</a> to tighten the figures.</>}
            {unmatchedCount > 0 && <> Match the till&rsquo;s products to dishes in <a href="/back-office/sales/import" className="underline">Import sales</a> and import again.</>}
          </Notice>
        )}

        {/* Budget vs Actual strip (Wave 2 → integrated) */}
        <BudgetStrip variance={budgetVariance} siteId={siteId} year={year} month={month} />

        {/* Summary tiles */}
        {liveTotals && (
          <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-3" data-testid="pnl-summary-tiles">
            <SummaryTile label="Revenue"    value={fmtMoney(liveTotals.revenue)}    delta={summary?.delta.revenue}                    deltaFormat="money" prevValue={summary?.previous.totals.revenue} vs={vs} />
            <SummaryTile label="Gross profit" value={fmtMoney(liveTotals.gross_profit)} hint={`${fmtPct(liveTotals.gp_margin)} margin`} />
            <SummaryTile label="EBITDA"     value={fmtMoney(liveTotals.ebitda)}     delta={summary?.delta.ebitda}                     deltaFormat="money" hint={`${fmtPct(liveTotals.ebitda_margin)} margin`} prevValue={summary?.previous.totals.ebitda} vs={vs} />
            <SummaryTile label="Net profit" value={fmtMoney(liveTotals.net_profit)} delta={summary?.delta.net_profit}                  deltaFormat="money" prevValue={summary?.previous.totals.net_profit} vs={vs} />
            <SummaryTile label="Net margin" value={fmtPct(liveTotals.net_margin)}   delta={summary?.delta.net_margin}                  deltaFormat="pp" prevValue={summary?.previous.totals.net_margin} vs={vs} />
            <FixedVariableTile fixed={liveTotals.fixed_costs} variable={liveTotals.variable_costs} />
          </div>
        )}

        {/* Benchmark strip (Wave 4C retrofit) */}
        {liveTotals && (
          <div className="bg-white border border-hairline/80 rounded-lg px-4 py-2.5 mb-5" data-testid="pnl-benchmark-strip">
            <BenchmarkPicker
              metric="revenue"
              currentValue={liveTotals.revenue}
              siteId={siteId}
              period={{
                start: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
                end:   new Date(Date.UTC(year, month, 1)).toISOString(),
              }}
              format="gbp"
              label="Compare Revenue vs"
            />
          </div>
        )}

        {/* P&L workbench */}
        <div className="grid xl:grid-cols-[1fr_360px] gap-4">
          {/* Lines table */}
          <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="pnl-lines-table">
            <div className="px-5 py-3 border-b border-hairline flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-ink">P&amp;L lines</h3>
                <p className="text-[12.5px] text-ink-soft mt-0.5">Auto-pulled values are pre-filled in grey. Type a number to override, or leave it for Verlo to keep them in sync.</p>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-ink-faint font-semibold">
                {liveLines.length} lines
              </div>
            </div>
            {loading && <div className="py-12 text-center text-ink-faint text-sm">Loading…</div>}
            {!loading && Object.entries(groups).map(([groupName, lines]) => {
              const groupTotal = lines.reduce((s, l) => s + l.value, 0);
              return (
                <div key={groupName} className="border-t border-hairline first:border-0">
                  <div className="px-5 py-2 bg-surface-2/60 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft">{groupName}</span>
                    <span className="text-[12px] font-mono-display font-semibold text-ink">{fmtMoney(groupTotal)}</span>
                  </div>
                  {lines.map((l) => (
                    <PnlLineRow
                      key={l.key}
                      line={l}
                      revenue={liveTotals?.revenue || 0}
                      onEdit={(patch) => editLine(l.key, patch)}
                      onReset={() => resetLine(l.key)}
                    />
                  ))}
                </div>
              );
            })}
            {/* Footer with computed totals */}
            {!loading && liveTotals && (
              <div className="border-t-2 border-hairline bg-surface-2/40 px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
                <SmallTotal label="COGS"   value={fmtMoney(liveTotals.cogs)} />
                <SmallTotal label="Opex"   value={fmtMoney(liveTotals.opex)} />
                <SmallTotal label="EBITDA" value={fmtMoney(liveTotals.ebitda)} accent />
                <SmallTotal label="Net"    value={fmtMoney(liveTotals.net_profit)} accent />
              </div>
            )}
          </div>

          {/* Right column: waterfall + insight */}
          <div className="space-y-4">
            <WaterfallCard waterfall={waterfall} year={year} month={month} />
            <Insight
              read={() => reportsIntelApi.pnlInsight({ year, month, ...(siteId !== 'all' ? { site_id: siteId } : {}) })}
              purpose={`What moved the bottom line in ${MONTH_LABELS[month - 1]} ${year}, and one check to run this week.`}
              testId="pnl-insight"
            />
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

// ---------- Sub-components ----------

const SummaryTile = ({ label, value, delta, deltaFormat = 'money', hint, prevValue, vs = 'last month' }) => {
  // Hide delta when previous period had effectively no data (£0 base = misleading "growth")
  const prevIsZero = prevValue !== undefined && Math.abs(prevValue) < 1;
  const showDelta = delta !== undefined && delta !== null && delta !== 0 && !prevIsZero;
  const good = delta != null && delta >= 0;
  return (
    <div className="bg-white border border-hairline/80 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft">{label}</div>
      <div className="font-mono-display text-2xl font-medium tabular-nums mt-0.5 text-ink">{value}</div>
      <div className="flex items-center gap-2 mt-0.5">
        {showDelta && (
          <span className={`inline-flex items-center gap-0.5 text-[12.5px] font-mono-display ${good ? 'text-signal-ok' : 'text-signal-stop'}`}>
            <ArrowUpRight size={10} className={good ? '' : 'rotate-90'} />
            {deltaFormat === 'pp' ? fmtPctSigned(delta) : fmtMoneySigned(delta)} vs {vs}
          </span>
        )}
        {!showDelta && prevIsZero && (
          <span className="text-[12.5px] text-ink-faint italic">First trading month</span>
        )}
        {hint && <span className="text-[12.5px] text-ink-soft">{hint}</span>}
      </div>
    </div>
  );
};

const FixedVariableTile = ({ fixed, variable }) => {
  const total = fixed + variable || 1;
  const fixedPct = (fixed / total) * 100;
  return (
    <div className="bg-white border border-hairline/80 rounded-lg p-4" data-testid="pnl-cost-mix">
      <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft">Cost mix</div>
      <div className="flex items-center gap-3 mt-2">
        <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
          <circle cx="18" cy="18" r="14" fill="none" stroke="#e2e8f0" strokeWidth="6" />
          <circle cx="18" cy="18" r="14" fill="none" stroke={BRAND.verlo} strokeWidth="6" strokeDasharray={`${(fixedPct / 100) * (2 * Math.PI * 14)} ${2 * Math.PI * 14}`} strokeLinecap="round" />
        </svg>
        <div className="text-[12.5px] leading-tight">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-md bg-verlo" /> <span className="text-ink-soft">Fixed</span> <span className="font-mono-display font-semibold text-ink ml-auto">{fmtMoney(fixed)}</span></div>
          <div className="flex items-center gap-1.5 mt-0.5"><span className="w-2 h-2 rounded-md bg-ink-faint" /> <span className="text-ink-soft">Variable</span> <span className="font-mono-display font-semibold text-ink ml-auto">{fmtMoney(variable)}</span></div>
        </div>
      </div>
    </div>
  );
};

const SmallTotal = ({ label, value, accent }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-ink-soft font-semibold">{label}</div>
    <div className={`font-mono-display tabular-nums ${accent ? 'text-base font-bold text-verlo' : 'text-sm font-semibold text-ink'}`}>{value}</div>
  </div>
);

const PnlLineRow = ({ line, revenue, onEdit, onReset }) => {
  const isAuto = !!line.auto;
  const canOverride = isAuto;
  const editing = line._dirty;
  const pct = revenue > 0 ? (line.value / revenue) * 100 : 0;
  const valueStr = line.value === 0 ? '' : String(line.value);

  return (
    <div className={`flex items-center gap-3 px-5 py-2 hover:bg-hover-tint/40 border-t border-hairline first:border-0 ${editing ? 'bg-signal-warn-tint/40' : ''}`} data-testid={`pnl-line-${line.key}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-medium text-ink">{line.label}</span>
          {isAuto && !line.manual_override && (
            <span className="inline-flex items-center gap-0.5 text-[11px] uppercase tracking-wider font-semibold text-ink-faint" title="Auto-pulled from Verlo data">
              <Lock size={9} />Auto
            </span>
          )}
          {line.manual_override && (
            <span className="text-[11px] uppercase tracking-wider font-semibold text-signal-warn">Override</span>
          )}
        </div>
      </div>
      <Select value={line.kind} onValueChange={(v) => onEdit({ kind: v })}>
        <SelectTrigger className="h-7 w-[100px] text-[12.5px]" data-testid={`pnl-kind-${line.key}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="variable">Variable</SelectItem>
          <SelectItem value="fixed">Fixed</SelectItem>
        </SelectContent>
      </Select>
      <div className="w-[110px]">
        <Input
          type="number"
          step="0.01"
          value={valueStr}
          placeholder={canOverride ? `Auto £${Math.round(line.auto_value).toLocaleString('en-GB')}` : '0'}
          onChange={(e) => onEdit({ value: e.target.value })}
          className="h-7 text-[12px] font-mono-display text-right"
          data-testid={`pnl-value-${line.key}`}
        />
      </div>
      <div className="w-[60px] text-right">
        <span className="text-[12.5px] text-ink-soft font-mono-display">{pct.toFixed(1)}%</span>
      </div>
      <button
        onClick={onReset}
        disabled={!editing}
        className="text-ink-faint hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
        title="Discard edit"
        data-testid={`pnl-reset-${line.key}`}
      >
        <RotateCcw size={12} />
      </button>
    </div>
  );
};

const WaterfallCard = ({ waterfall, year, month }) => {
  if (!waterfall) return null;
  const steps = waterfall.steps || [];
  if (!steps.length) return null;
  // Compute running EBITDA value at each step for vertical bar positioning
  let running = 0;
  const points = steps.map((s, i) => {
    let bottom = 0;
    let top = 0;
    if (s.kind === 'start') { running = s.value; top = s.value; bottom = 0; }
    else if (s.kind === 'end') { top = s.value; bottom = 0; running = s.value; }
    else {
      const prev = running;
      running += s.value;
      bottom = Math.min(prev, running);
      top = Math.max(prev, running);
    }
    return { ...s, bottom, top, running };
  });
  const allVals = points.flatMap((p) => [p.bottom, p.top]).concat([0]);
  const yMin = Math.min(...allVals);
  const yMax = Math.max(...allVals);
  const yRange = (yMax - yMin) || 1;
  const W = 360, H = 220, padX = 12, padY = 14;
  const barW = Math.max(8, (W - padX * 2) / points.length - 4);
  return (
    <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="pnl-waterfall">
      <div className="px-4 py-3 border-b border-hairline">
        <h3 className="text-sm font-semibold text-ink">EBITDA waterfall</h3>
        <p className="text-[12.5px] text-ink-soft mt-0.5">Prev month → {month}/{year} · what each line contributed</p>
      </div>
      <div className="px-2 py-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
          {/* Zero line */}
          <line x1={padX} x2={W - padX} y1={padY + ((yMax - 0) / yRange) * (H - padY * 2)} y2={padY + ((yMax - 0) / yRange) * (H - padY * 2)} stroke="#cbd5e1" strokeDasharray="2 3" />
          {points.map((p, i) => {
            const xCenter = padX + (W - padX * 2) * ((i + 0.5) / points.length);
            const yTop = padY + ((yMax - p.top) / yRange) * (H - padY * 2);
            const yBot = padY + ((yMax - p.bottom) / yRange) * (H - padY * 2);
            const height = Math.max(1, yBot - yTop);
            const isStartEnd = p.kind === 'start' || p.kind === 'end';
            const color = isStartEnd ? BRAND.verlo : (p.direction === 'good' ? BRAND.signal.ok.line : BRAND.signal.stop.line);
            return (
              <g key={i}>
                <rect x={xCenter - barW / 2} y={yTop} width={barW} height={height} fill={color} rx={2} opacity={isStartEnd ? 1 : 0.85} />
                {p.kind === 'delta' && (
                  <text x={xCenter} y={yTop - 2} textAnchor="middle" fontSize="8" fill="#475569">
                    {p.value >= 0 ? '+' : ''}£{Math.abs(p.value) >= 1000 ? `${(p.value / 1000).toFixed(1)}k` : Math.round(p.value)}
                  </text>
                )}
                {isStartEnd && (
                  <text x={xCenter} y={yTop - 2} textAnchor="middle" fontSize="8" fill={BRAND.verlo} fontWeight="600">
                    £{Math.round(p.value).toLocaleString('en-GB')}
                  </text>
                )}
                <text x={xCenter} y={H - 3} textAnchor="middle" fontSize="7" fill="#94a3b8" transform={`rotate(-25 ${xCenter} ${H - 3})`}>
                  {p.label.length > 12 ? p.label.slice(0, 11) + '…' : p.label}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="flex items-center justify-between px-2 pt-2 border-t border-hairline">
          <span className="text-[12.5px] text-ink-soft">Net EBITDA change</span>
          <span className={`text-[13px] font-mono-display font-semibold inline-flex items-center gap-1 ${waterfall.ebitda_delta >= 0 ? 'text-signal-ok' : 'text-signal-stop'}`}>
            {waterfall.ebitda_delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {fmtMoneySigned(waterfall.ebitda_delta)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PnlPage;

// ---------- Budget vs Actual strip (Wave 2 → integrated into P&L) ----------

const BudgetStrip = ({ variance, siteId, year, month }) => {
  if (!variance || siteId === 'all') {
    // Compact "empty state" that still teases the integration.
    return (
      <div className="bg-surface-2 border border-hairline rounded-lg px-4 py-2.5 mb-4 flex items-center justify-between text-[12px]" data-testid="pnl-budget-strip-empty">
        <span className="text-ink-soft">
          {siteId === 'all'
            ? 'Pick a single site to see budget variance for this month.'
            : 'No budget set for this period yet.'}
        </span>
        <a href="/back-office/reports/budget" className="text-[12.5px] font-semibold text-verlo hover:text-verlo-hover inline-flex items-center gap-0.5" data-testid="pnl-budget-strip-cta">
          {siteId === 'all' ? 'Open Budget' : 'Set budget'}
          <ChevronRight size={12} />
        </a>
      </div>
    );
  }
  const breaches = variance.rows.filter((r) => r.status === 'bad');
  const watchers = variance.rows.filter((r) => r.status === 'warn');
  const onTrack = variance.rows.filter((r) => r.status === 'good');
  const budgetedCount = variance.rows.filter((r) => r.monthly_target !== null).length;

  const pillCls = breaches.length > 0
    ? 'bg-signal-stop-tint border-signal-stop-line text-signal-stop'
    : watchers.length > 0
      ? 'bg-signal-warn-tint border-signal-warn-line text-signal-warn'
      : 'bg-signal-ok-tint border-signal-ok-line text-signal-ok';

  return (
    <div className={`border rounded-lg px-4 py-2.5 mb-4 flex flex-wrap items-center gap-3 ${pillCls}`} data-testid="pnl-budget-strip">
      <span className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-white/60 border border-current/20">
        Budget · {variance.month_progress_pct}% of month elapsed
      </span>
      <span className="text-[12px]">
        {budgetedCount === 0
          ? 'No KPIs budgeted for this month yet.'
          : (
            <>
              <b>{onTrack.length} on track</b>
              {watchers.length > 0 && <> · <b>{watchers.length} at risk</b></>}
              {breaches.length > 0 && <> · <b>{breaches.length} missing target</b></>}
              {' '}({budgetedCount} of {variance.rows.length} KPIs budgeted)
            </>
          )}
      </span>
      <a
        href={`/back-office/reports/budget?site=${encodeURIComponent(siteId)}&year=${year}&month=${month}`}
        className="ml-auto text-[12.5px] font-semibold inline-flex items-center gap-0.5 hover:opacity-70"
        data-testid="pnl-budget-strip-cta"
      >
        {budgetedCount === 0 ? 'Set budgets' : 'Budget detail'}
        <ChevronRight size={12} />
      </a>
    </div>
  );
};
